import WebRTCClient from './WebRTCClient'

import EventEmitter from '../../common/EventEmitter'
import Logger from '../../common/Logger'

import {MEETINGS_API_EVENTS} from '../../api/roschat/protocols/meetings'

export const CONFERENCE_STATES = {
    UNKNOWN: 'unknown',
    ONLINE: 'online',
    OFFLINE: 'offline',
    WAITING: 'waiting',
    RECONNECTING: 'reconnecting',
}

export const CONNECTION_STATUSES = {
    DISCONNECTED: 'disconnected',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
}

export const CLIENT_EVENTS = {
    CONNECTION_STATUS_CHANGED: 'connection-status-changed',
    CONF_STATE_CHANGED: 'conf-state-changed',
    CONF_OPTIONS_CHANGED: 'conf-options-changed',
    CONF_PARTICIPANTS_INITED: 'conf-participants-inited',
    CONF_PARTICIPANT_ADDED: 'conf-participant-add',
    CONF_PARTICIPANT_UPDATED: 'conf-participant-updated',
    CONF_PARTICIPANT_REMOVED: 'conf-participant-removed',
    CONF_DEVICES_CHANGED: 'conf-devices-changed',
    CONF_COMMAND: 'conf-command',
    SHARE_STARTED: 'share-started',
    SHARE_ENDED: 'share-ended',
}

const DEFAULT_CONF_OPTIONS = {
    audio: true,
    video: false,
    waiting: false,
    atOnce: false,
}

const PARTICIPANT_CONNECTION_STATE = {
    DISCONNECTED: 'disconnected',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
}

export const PARTICIPANT_PROPS = {
    ID: 'id',
    CID: 'cid',
    NAME: 'name',
    ROLE: 'role',
    BANNED: 'banned',
    CAM: 'cam',
    MIC: 'mic',
    CAN_CAM: 'canCam',
    CAN_MIC: 'canMic',
    LET_ME_SPEAK: 'letMeSpeak',

    CONNECTION_STATE: 'connectionState',
    OWNER: 'owner',
    MODERATOR: 'moderator',
    IS_ME: 'isMe',
}

export const SHARING_PROPS = {
    MEETING_ID: 'meetingId',
    USER_ID: 'userId',
    ID: 'id',

    MY_SHARING: 'mySharing',
    WAIT_OFFER: 'waitOffer'
}

export const PARTICIPANT_ROLES = {
    USER: 'user',
    ORGANIZER: 'organizer',
    MODERATOR: 'moderator',
}


const MEDIA_DEVICES = {
    AUDIO_INPUT: 'audioinput',
    AUDIO_OUTPUT: 'audiooutput',
    VIDEO_INPUT: 'videoinput',
}

const LOG_STRING_LIMIT = 2000
const LOG_ARRAY_LIMIT = 20

const describeTrack = (track) => {
    if (!track) return null
    return {
        id: track.id,
        kind: track.kind,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
        label: track.label,
    }
}

const describeStream = (stream) => {
    if (!stream) return null
    const tracks = (stream.getTracks && stream.getTracks()) || []
    return {
        id: stream.id,
        active: stream.active,
        tracks: tracks.map((track) => describeTrack(track)),
    }
}

const describeMediaElement = (el) => {
    if (!el) return null
    return {
        tag: el.tagName,
        id: el.id,
        muted: el.muted,
        volume: el.volume,
        readyState: el.readyState,
        paused: el.paused,
    }
}

const safeStringify = (value) => {
    if (value === undefined) return 'undefined'
    if (value === null) return 'null'
    if (typeof value === 'string') return value
    if (typeof value !== 'object') return String(value)
    const seen = new WeakSet()
    const replacer = (key, val) => {
        if (key === 'sdp' && typeof val === 'string') return `<sdp:${val.length}>`
        if (typeof val === 'bigint') return val.toString()
        if (typeof val === 'function') return `[Function${val.name ? ':' + val.name : ''}]`
        if (Array.isArray(val) && val.length > LOG_ARRAY_LIMIT) {
            return {length: val.length, preview: val.slice(0, LOG_ARRAY_LIMIT)}
        }
        if (val instanceof Error) {
            return {message: val.message, stack: val.stack}
        }
        if (typeof MediaStream !== 'undefined' && val instanceof MediaStream) {
            return describeStream(val)
        }
        if (typeof MediaStreamTrack !== 'undefined' && val instanceof MediaStreamTrack) {
            return describeTrack(val)
        }
        if (typeof RTCPeerConnection !== 'undefined' && val instanceof RTCPeerConnection) {
            return {
                connectionState: val.connectionState,
                iceConnectionState: val.iceConnectionState,
                signalingState: val.signalingState,
            }
        }
        if (typeof RTCSessionDescription !== 'undefined' && val instanceof RTCSessionDescription) {
            return {type: val.type, sdpLength: val.sdp ? val.sdp.length : 0}
        }
        if (typeof RTCIceCandidate !== 'undefined' && val instanceof RTCIceCandidate) {
            return {candidate: val.candidate, sdpMid: val.sdpMid, sdpMLineIndex: val.sdpMLineIndex}
        }
        if (typeof MediaDeviceInfo !== 'undefined' && val instanceof MediaDeviceInfo) {
            return {kind: val.kind, deviceId: val.deviceId, label: val.label, groupId: val.groupId}
        }
        if (typeof HTMLMediaElement !== 'undefined' && val instanceof HTMLMediaElement) {
            return describeMediaElement(val)
        }
        if (val instanceof Map) return Array.from(val.entries())
        if (val instanceof Set) return Array.from(val.values())
        if (val && typeof val === 'object') {
            if (seen.has(val)) return '[Circular]'
            seen.add(val)
        }
        return val
    }
    try {
        const str = JSON.stringify(value, replacer)
        if (str && str.length > LOG_STRING_LIMIT) {
            return `${str.slice(0, LOG_STRING_LIMIT)}...<truncated>`
        }
        return str
    } catch (e) {
        return '[Unserializable]'
    }
}

const buildLogMessage = (message, data) => {
    if (data === undefined) return message
    return `${message} | ${safeStringify(data)}`
}

export const CONF_PROPS = {
    ID: 'id',
    TOPIC: 'topic',
    AUTHOR: 'author',
    START_TIME: 'startTime',
    DURATION: 'duration',
}

class MeetingsClient extends EventEmitter {
    constructor ({
                     transport,
                     meetingId,
                     password = '',
                     name = '',
                     activeDevices,
                     logger,
                     debug = true
                 }) {
        super()
        if (!meetingId) throw new Error('meetingId should be set')
        this.el = null
        this.share = null
        this.sharing = null
        this.videoWrapperEl = null
        this.iceServers = []
        this.transport = transport
        this.meetingId = meetingId
        this.password = password
        this.name = name
        this.activeDevices = activeDevices || {}
        this.mic = false
        this.cam = false
        this.connectionStatus = CONNECTION_STATUSES.DISCONNECTED
        this.confState = CONFERENCE_STATES.UNKNOWN
        this.participants = new Map()
        this.meetingUserId = ''
        this.confOptions = DEFAULT_CONF_OPTIONS
        this.meetingInitAttempts = 30
        this.webRTCClient = null
        this.settings = {}
        this.audioMixing = false
        this.subscribeMode = false
        this.pendingOffers = new Map()
        this.debug = debug
        this.logger = logger || new Logger('MeetingsClient')
        this.webrtcLogger = logger ? (logger instanceof Logger ? new Logger('WebRTCClient') : logger) : new Logger('WebRTCClient')
        this._init()
    }

    _init () {
        this._subscribeOnTransport()
    }

    _logInfo(message, data) {
        const text = buildLogMessage(message, data)
        if (this.logger.info) this.logger.info(text)
        else console.log(text)
    }

    _logWarn(message, data) {
        const text = buildLogMessage(message, data)
        if (this.logger.warn) this.logger.warn(text)
        else this.logger.info(text)
    }

    _logDebug(message, data) {
        if (!this.debug) return
        const text = buildLogMessage(message, data)
        if (this.logger.info) this.logger.info(text)
        else console.log(text)
    }

    _logError(error, description, data) {
        const desc = description ? buildLogMessage(description, data) : (data !== undefined ? buildLogMessage('error', data) : undefined)
        if (this.logger.error) this.logger.error(error, desc)
        else this.logger.info(buildLogMessage('error', {error: String(error), description: desc}))
    }

    _logStreamIssues(label, stream, expected = {}) {
        if (!stream) {
            this._logWarn(`${label}: stream missing`, expected)
            return
        }
        const audioTracks = stream.getAudioTracks ? stream.getAudioTracks() : []
        const videoTracks = stream.getVideoTracks ? stream.getVideoTracks() : []
        const audioLive = audioTracks.some((track) => track.enabled && track.readyState === 'live')
        const videoLive = videoTracks.some((track) => track.enabled && track.readyState === 'live')
        if (expected.audio && audioTracks.length === 0) {
            this._logWarn(`${label}: audio track missing`, describeStream(stream))
        } else if (expected.audio && !audioLive) {
            this._logWarn(`${label}: audio track not live`, {tracks: audioTracks.map(describeTrack)})
        }
        if (expected.video && videoTracks.length === 0) {
            this._logWarn(`${label}: video track missing`, describeStream(stream))
        } else if (expected.video && !videoLive) {
            this._logWarn(`${label}: video track not live`, {tracks: videoTracks.map(describeTrack)})
        }
    }

    _subscribeOnTransport () {
        this.transport.on(MEETINGS_API_EVENTS.CHANGE_MEETING, () => {

        })
        this.transport.on(MEETINGS_API_EVENTS.DELETE_MEETING, () => {

        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_STATE, (payload) => {
            const { meetingId, state } = payload
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.MEETING_STATE}`, payload)
            if (this.meetingId === meetingId) this._setConfState(state)
        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_CONNECT, (payload) => {
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.MEETING_CONNECT}`, payload)
            this._onParticipantConnected(payload)
        })
        this.transport.on(MEETINGS_API_EVENTS.CHANGE_MEETING_USER, (payload) => {
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.CHANGE_MEETING_USER}`, payload)
            this._onParticipantChanged(payload)
        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_DISCONNECT, (payload) => {
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.MEETING_DISCONNECT}`, payload)
            this._onParticipantDisconnected(payload)
        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_ANSWER, (payload) => {
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.MEETING_ANSWER}`, payload)
            this._onMeetingAnswer(payload)
        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_OFFER, async (payload) => {
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.MEETING_OFFER}`, payload)
            this._onMeetingOffer(payload)
        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_ADD_CANDIDATE, (payload) => {
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.MEETING_ADD_CANDIDATE}`, payload)
            this._onMeetingAddCandidate(payload)
        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_REMOVE_CANDIDATE, (payload) => {
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.MEETING_REMOVE_CANDIDATE}`, payload)
            this._onMeetingRemoveCandidate(payload)
        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_INVITE_USER, () => {

        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_REJECT_USER, () => {

        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_INVITE, () => {

        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_COMMAND, (payload) => {
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.MEETING_COMMAND}`, payload)
            this.emit(CLIENT_EVENTS.CONF_COMMAND, payload)
        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_SHARING_START, (payload) => {
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.MEETING_SHARING_START}`, payload)
            this._onSharingStart(payload)
        })
        this.transport.on(MEETINGS_API_EVENTS.MEETING_SHARING_STOP, (payload) => {
            this._logDebug(`transport event - ${MEETINGS_API_EVENTS.MEETING_SHARING_STOP}`, payload)
            this._onSharingStop(payload)
        })
    }

    getActiveConfId () {
        return this.meetingId
    }

    getSettings() {
        return this.settings
    }

    async reconnect () {
        this._logInfo('reconnect: start', {meetingId: this.meetingId})
        this._setConfState(CONFERENCE_STATES.RECONNECTING)
        await this.meetingEnter()
        this._setConfState(CONFERENCE_STATES.ONLINE)
        this._logInfo('reconnect: done', {meetingId: this.meetingId})
    }

    async meetingEnter() {
        this._logInfo('meetingEnter: start', {meetingId: this.meetingId, mic: this.mic, cam: this.cam})
        this._initDOM()
        try {
            let meetingInfo = await this.transport.meetingInfo({
                meetingId: this.meetingId,
                password: this.password,
            })
            this._logDebug('meetingEnter: meetingInfo', meetingInfo)

            if (meetingInfo.settings && !meetingInfo.settings.video) {
                this.cam = false
                this._logWarn('meetingEnter: video disabled by settings', meetingInfo.settings)
            }

            this._logDebug('meetingEnter: transport meetingEnter called')
            let response = await this.transport.meetingEnter({
                meetingId: this.meetingId,
                password: this.password,
                name: this.name,
                mic: this.mic,
                cam: this.cam,
            })
            this._logDebug('meetingEnter: transport meetingEnter response received', response)

            this.settings = response
            this.meetingUserId = response.id
            this._updateParticipants(response.contacts)
            this.sharing = response.sharing && this._extendSharingProps(response.sharing)
            this.audioMixing = Boolean(response && response.settings && response.settings.audioMixing)
            this.subscribeMode = Boolean(response && response.subscribe)
            this._logDebug('meetingEnter: settings', {
                subscribeMode: this.subscribeMode,
                audioMixing: this.audioMixing,
                settings: response.settings,
            })

            let iceServer = await this.transport.meetingGetIceServers()
            this.iceServers = [{urls: 'turn:' + iceServer.server, username: iceServer.username, credential: iceServer.password}]
            this._logDebug('meetingEnter: iceServers formed', this.iceServers)
            if (this.webRTCClient) this.webRTCClient.setIceServers(this.iceServers)

            this._setConfState(response.state)
        } catch (e) {
            this._logError(e, 'meetingEnter: transport meetingEnter error')
            throw e
        }
    }

    async join (payload) {
        this._logInfo('join', {
            meetingId: this.meetingId,
            mic: payload?.mic,
            cam: payload?.cam,
            hasEl: Boolean(payload?.el),
            hasShareEl: Boolean(payload?.share),
        })
        const {el, share = null, mic = false, cam = false} = payload
        this.el = el
        this.share = share
        this.mic = mic
        this.cam = cam
        await this.meetingEnter()
    }

    leave() {
        this._logInfo('leave', {meetingId: this.meetingId})
        this.transport.meetingExit({meetingId: this.meetingId})
        this._removeAllParticipants()
        while (this.el && this.el.firstChild) this.el.removeChild(this.el.firstChild)
        while (this.share && this.share.firstChild) this.share.removeChild(this.share.firstChild)
        this._removeAudioMixingDomElement()
        this.pendingOffers.clear()
        if (this.webRTCClient) this.webRTCClient.stop()
    }

    async changeMediaDevice (kind, val) {
        this.activeDevices[kind] = val
        this._logDebug('changeMediaDevice', {kind, deviceId: val})

        switch (kind) {
            case MEDIA_DEVICES.AUDIO_INPUT:
                await this._changeMicrophone(val)
                break
            case MEDIA_DEVICES.VIDEO_INPUT:
                await this._changeCamera(val)
                break
            case MEDIA_DEVICES.AUDIO_OUTPUT:
                await this._changeSpeaker(val)
                break
        }

        this.emit(CLIENT_EVENTS.CONF_DEVICES_CHANGED, {...this.activeDevices})
    }

    async _changeMicrophone (id) {
        if (this.webRTCClient) {
            try {
                const result = await this.webRTCClient.replaceLocalAudioDevice(id)
                this._logDebug('replaceLocalAudioDevice: done', result)
            } catch (e) {
                this._logError(e, 'replaceLocalAudioDevice: failed', {deviceId: id})
            }
        }

    }

    async _changeCamera (id) {
        if (this.webRTCClient) {
            try {
                const result = await this.webRTCClient.replaceLocalVideoDevice(id)
                this._logDebug('replaceLocalVideoDevice: done', result)
            } catch (e) {
                this._logError(e, 'replaceLocalVideoDevice: failed', {deviceId: id})
            }
        }
    }

    async _changeSpeaker(id) {
        if (this.webRTCClient) {
            this.webRTCClient.replaceRemoteAudioDevice(id)
            this._logDebug('replaceRemoteAudioDevice', {deviceId: id})
        }
    }

    async toggleCamera () {
        if (this.webRTCClient) {
            let isMuted = this.webRTCClient.muteVideo()
            this.cam = !isMuted
            if (this.cam && this.webRTCClient.videoPlug) {
                await this._changeCamera(this.activeDevices[MEDIA_DEVICES.VIDEO_INPUT])
            }
            this.changeParticipantProps({meetingId: this.meetingId, id: this.meetingUserId, props: {cam: !isMuted}})
            this._logDebug('toggleCamera', {cam: this.cam, muted: isMuted})
            this._logStreamIssues('local stream after toggleCamera', this.webRTCClient.getLocalStream(), {video: this.cam})
        }
    }

    async toggleMicrophone () {
        if (this.webRTCClient) {
            let isMuted = this.webRTCClient.mute()
            this.mic = !isMuted
            if (this.mic && this.webRTCClient.audioPlug) {
                await this._changeMicrophone(this.activeDevices[MEDIA_DEVICES.AUDIO_INPUT])
            }
            this.changeParticipantProps({meetingId: this.meetingId, id: this.meetingUserId, props: {mic: !isMuted}})
            this._logDebug('toggleMicrophone', {mic: this.mic, muted: isMuted})
            this._logStreamIssues('local stream after toggleMicrophone', this.webRTCClient.getLocalStream(), {audio: this.mic})
        }
    }

    toggleSpeaker () {
        if (this.webRTCClient) {
            const muted = this.webRTCClient.muteSpeaker()
            this._logDebug('toggleSpeaker', {muted})
            return muted
        }
    }

    changeSpeakerVolume(volume) {
        if (this.webRTCClient) {
            this.webRTCClient.changeSpeakerVolume(volume)
            this._logDebug('changeSpeakerVolume', {volume})
        }
    }

    changeParticipantProps({meetingId, id, props}) {
        if (this.meetingId !== meetingId) return
        this._logDebug('changeParticipantProps', {id, props})
        this.transport.changeMeetingUser({meetingId: this.meetingId, id, ...props})
    }

    sendCommand (payload) {
        this._logDebug('sendCommand', payload)
        this.transport.sendMeetingCommand({meetingId: this.meetingId, ...payload})
    }

    async shareToggle () {
        if (this.webRTCClient) {
            if (!this.sharing) await this.startSharing()
            else await this.stopSharing()
        }
    }

    async startSharing () {
        if (!this.sharing && this.webRTCClient) {
            let stream = await this.webRTCClient.startLocalSharing()

            let { id } = await this.transport.meetingSharingStart({meetingId: this.meetingId})
            this.sharing = this._extendSharingProps({userId: this.meetingUserId, id})
            this._logDebug('startSharing', {id: this.sharing.id, stream: describeStream(stream)})
            this.emit(CLIENT_EVENTS.SHARE_STARTED, { ...this.sharing })
            this.sharing.direction = 'sendonly'
            this.sharing.localStream = stream
            await this._initConferenceMedia(this.sharing)
            stream.getVideoTracks().forEach((track) => track.addEventListener('ended', () => this.stopSharing()))
        }
    }

    async stopSharing () {
        if (this.sharing && this.sharing.userId === this.meetingUserId) {
            this._logDebug('stopSharing', {id: this.sharing.id})
            this.webRTCClient.setLocalSharingStream()
            await this.transport.meetingSharingStop({meetingId: this.meetingId, id: this.sharing.id})
            if (this.webRTCClient) this.webRTCClient.stopPeer(this.sharing.id)
            this.emit(CLIENT_EVENTS.SHARE_ENDED)
            this.sharing = null
        }
    }

    _initDOM () {
        {
            let remoteViewWrapper = document.createElement('div')
            remoteViewWrapper.setAttribute('class', 'remoteViewWrapper')
            remoteViewWrapper.setAttribute('style', 'width: 100%; height: 100%;')
            let remotesLayout = document.createElement('div')
            remotesLayout.setAttribute('class', 'remotes_layout')
            remoteViewWrapper.appendChild(remotesLayout)
            let videosClass = document.createElement('div')
            videosClass.setAttribute('class', 'videos_class')
            remotesLayout.appendChild(videosClass)
            this.el.appendChild(remoteViewWrapper)
            this.videoWrapperEl = videosClass
        }

        {
            let remoteSharingWrapper = document.createElement('div')
            remoteSharingWrapper.setAttribute('class', 'remoteSharingWrapper')
            remoteSharingWrapper.setAttribute('style', 'width: 100%; height: 100%;')
            let remotesLayout = document.createElement('div')
            remotesLayout.setAttribute('class', 'remotes_layout')
            remotesLayout.setAttribute('style', 'width: 100%; height: 100%;')
            remoteSharingWrapper.appendChild(remotesLayout)
            let videosClass = document.createElement('div')
            videosClass.setAttribute('class', 'videos_class')
            videosClass.setAttribute('style', 'width: 100%; height: 100%;')
            remotesLayout.appendChild(videosClass)
            this.share.setAttribute('style', 'width: 100%; height: 100%; display: none;')
            this.share.appendChild(remoteSharingWrapper)
            this.shareWrapperEl = videosClass
        }
    }

    _onMeetingAnswer ({meetingId, id, sdp}) {
        if (meetingId !== this.meetingId) return
        this._logDebug('_onMeetingAnswer', {id, sdp})
        const answer = {type: 'answer', sdp}
        let participant = this._getParticipant(id)
        if (participant) {
            if (this.webRTCClient) this.webRTCClient.setRemoteAnswer(id, answer)
            participant[PARTICIPANT_PROPS.CONNECTION_STATE] = PARTICIPANT_CONNECTION_STATE.CONNECTED
        } else {
            this._logWarn('_onMeetingAnswer: participant not found', {id})
        }
    }

    async _onMeetingOffer ({meetingId, id, sdp}) {
        if (this.subscribeMode && this.meetingId === meetingId) {
            const participant = this._getParticipant(id)
            const offer = {type: 'offer', sdp}
            if (participant) {
                participant.offer = offer
                if (this.confState === CONFERENCE_STATES.ONLINE) {
                    if (this.sharing === participant) {
                        await this._connectSharing()
                    } else {
                        const connectionState = participant[PARTICIPANT_PROPS.CONNECTION_STATE]
                        if (!participant.videoElId || connectionState === PARTICIPANT_CONNECTION_STATE.DISCONNECTED) {
                            await this._connectParticipant(id)
                            return
                        }
                        this._scheduleInitConferenceMedia(participant)
                    }
                }
            } else {
                this._bufferMeetingOffer(id, offer)
                this._logDebug('_onMeetingOffer: participant not found, offer buffered', {id, offer})
            }
        } else {
            this._logDebug('_onMeetingOffer: offer ignored', {meetingId, id})
        }
    }

    getParticipantsList() {
        return [ ...this.participants.values() ]
    }

    _getParticipant(id) {
        let participant = this.participants.get(id)
        if (!participant && this.sharing?.id === id) participant = this.sharing
        return participant
    }

    _bufferMeetingOffer(id, offer) {
        if (!id || !offer) return
        this.pendingOffers.set(id, offer)
        this._logDebug('bufferMeetingOffer', {id})
    }

    _takeBufferedOffer(id) {
        if (!id) return null
        const offer = this.pendingOffers.get(id)
        if (offer) {
            this.pendingOffers.delete(id)
            this._logDebug('buffered offer consumed', {id})
        }
        return offer
    }

    _clearBufferedOffer(id) {
        if (!id) return
        this.pendingOffers.delete(id)
    }

    _onMeetingAddCandidate ({meetingId, id, candidate}) {
        if (meetingId !== this.meetingId) return
        this._logDebug('_onMeetingAddCandidate', {id, candidate})
        const participant = this._getParticipant(id)
        if (participant && this.webRTCClient) {
            this.webRTCClient.addIceCandidate(id, candidate)
        } else {
            this._logWarn('_onMeetingAddCandidate: participant not found', {id})
        }
    }

    _onMeetingRemoveCandidate ({meetingId, id, candidate}) { //@todo
        /*if (meetingId !== this.meetingId) return
        let participant = this._getParticipant(id)
        if (participant) participant.peerConnection.removeCandidate(candidate)*/
    }

    _onParticipantConnected ({meetingId, ...participant}) {
        if (meetingId !== this.meetingId) return
        this._logDebug('_onParticipantConnected', participant)
        this._addParticipant(participant)
        this._connectParticipant(participant[PARTICIPANT_PROPS.ID])
    }

    _onParticipantChanged ({meetingId, ...participant}) {
        let participantOld = this._getParticipant(participant[PARTICIPANT_PROPS.ID])
        if (meetingId !== this.meetingId || !participantOld) return
        let participantNew = this._extendParticipantProps(participant)
        Object.assign(participantOld, participantNew)
        this._logDebug('_onParticipantChanged', participantNew)
        this.emit(CLIENT_EVENTS.CONF_PARTICIPANT_UPDATED, {...participantOld})
    }

    _onParticipantDisconnected ({meetingId, id, reason}) {
        if (meetingId !== this.meetingId) return
        this._logDebug('_onParticipantDisconnected', {id, reason})
        if (this.meetingUserId === id) {
            this._setConfState(CONFERENCE_STATES.OFFLINE, reason)
        } else {
            this._removeParticipant(id)
        }
    }

    async _onSharingStart (payload) {
        const {meetingId, userId, id} = payload
        this._logDebug('_onSharingStart', payload)
        if (this.meetingId !== meetingId) return
        this.sharing = this._extendSharingProps(payload)
        const pendingOffer = this._takeBufferedOffer(id)
        if (pendingOffer) this.sharing.offer = pendingOffer
        this.emit(CLIENT_EVENTS.SHARE_STARTED, { ...this.sharing })
        await this._connectSharing()
    }

    _onSharingStop (payload) {
        const {meetingId, id} = payload
        if (this.meetingId !== meetingId) return
        this._logDebug('_onSharingStop', payload)
        this.emit(CLIENT_EVENTS.SHARE_ENDED)
        if (this.webRTCClient) {
            const shareId = id || this.sharing?.id
            if (shareId) this.webRTCClient.stopPeer(shareId)
        }
        this.sharing = null
        if (id) this._clearBufferedOffer(id)
        this.webRTCClient.setSharingStream()
        this._removeShareDomElements()
    }

    _setConnectionStatus (status) {
        if (this.connectionStatus !== status) {
            this.connectionStatus = status
            this._logInfo('connectionStatus changed', {status})
            this.emit(CLIENT_EVENTS.CONNECTION_STATUS_CHANGED, status)
        }
    }

    _setConfState (state, reason) {
        if (this.confState !== state) {
            this.confState = state
            this._logInfo('confState changed', {state, reason})
            switch (state) {
                case CONFERENCE_STATES.ONLINE: {
                    this._startTranslation()
                    break
                }
                case CONFERENCE_STATES.OFFLINE:
                case CONFERENCE_STATES.RECONNECTING: {
                    this._stopTranslation()
                    break
                }
                case CONFERENCE_STATES.WAITING: {

                    break
                }
            }
            this.emit(CLIENT_EVENTS.CONF_STATE_CHANGED, {state, reason})
        }
    }

    _updateConfOptions (options) {
        this.confOptions = {...DEFAULT_CONF_OPTIONS, ...options}
        this.emit(CLIENT_EVENTS.CONF_OPTIONS_CHANGED, {...this.confOptions})
    }

    _updateParticipants (participantList) {
        this.participants = new Map()
        participantList.forEach((participant) => this._addParticipant(participant))
        this._logDebug('_updateParticipants', {count: participantList.length})
        this.emit(CLIENT_EVENTS.CONF_PARTICIPANTS_INITED, this.getParticipantsList())
    }

    _extendParticipantProps (participant) {
        let props = {
            [PARTICIPANT_PROPS.CAM]: (PARTICIPANT_PROPS.CAM in participant) ? participant[PARTICIPANT_PROPS.CAM] : false,
            [PARTICIPANT_PROPS.MIC]: (PARTICIPANT_PROPS.MIC in participant) ? participant[PARTICIPANT_PROPS.MIC] : false,
            [PARTICIPANT_PROPS.CAN_CAM]: (PARTICIPANT_PROPS.CAN_CAM in participant) ? participant[PARTICIPANT_PROPS.CAN_CAM] : true,
            [PARTICIPANT_PROPS.CAN_MIC]: (PARTICIPANT_PROPS.CAN_MIC in participant) ? participant[PARTICIPANT_PROPS.CAN_MIC] : true,
            [PARTICIPANT_PROPS.LET_ME_SPEAK]: (PARTICIPANT_PROPS.LET_ME_SPEAK in participant) ? participant[PARTICIPANT_PROPS.LET_ME_SPEAK] : false,

            //[PARTICIPANT_PROPS.CONNECTION_STATE]: participant[PARTICIPANT_PROPS.CONNECTION_STATE] ?? PARTICIPANT_CONNECTION_STATE.DISCONNECTED,
            [PARTICIPANT_PROPS.OWNER]: participant[PARTICIPANT_PROPS.ROLE] === PARTICIPANT_ROLES.ORGANIZER,
            [PARTICIPANT_PROPS.MODERATOR]: [PARTICIPANT_ROLES.ORGANIZER, PARTICIPANT_ROLES.MODERATOR].includes(participant[PARTICIPANT_PROPS.ROLE]),
            [PARTICIPANT_PROPS.IS_ME]: participant.id === this.meetingUserId,
        }
        if (this.settings && this.settings.settings && !this.settings.settings.video) props[PARTICIPANT_PROPS.CAN_CAM] = false
        return Object.assign(participant, props)
    }

    _extendSharingProps (sharing) {
        const mySharing = sharing[SHARING_PROPS.USER_ID] === this.meetingUserId
        return Object.assign(sharing, {
            connectionState: PARTICIPANT_CONNECTION_STATE.DISCONNECTED,
            [SHARING_PROPS.MEETING_ID]: this.meetingId,
            [SHARING_PROPS.MY_SHARING]: mySharing,
            [SHARING_PROPS.WAIT_OFFER]: !mySharing && this.subscribeMode,
        })
    }

    _addParticipant (participant) {
        this._logDebug('_addParticipant', participant)
        if (!this.participants.has(participant[PARTICIPANT_PROPS.ID])) {
            participant = this._extendParticipantProps(participant)
            const pendingOffer = this._takeBufferedOffer(participant[PARTICIPANT_PROPS.ID])
            if (pendingOffer && !participant.offer) participant.offer = pendingOffer
            participant[PARTICIPANT_PROPS.CONNECTION_STATE] = PARTICIPANT_CONNECTION_STATE.DISCONNECTED
            this.participants.set(participant[PARTICIPANT_PROPS.ID], participant)
        }
    }

    _removeParticipant (id) {
        const participant = this._getParticipant(id)
        if (participant) {
            this._logDebug('_removeParticipant', {id})
            this._removeParticipantDomElements(id)
            this.participants.delete(id)
            this._clearBufferedOffer(id)
            if (this.webRTCClient) {
                this.webRTCClient.stopPeer(id)
                this.webRTCClient.removeRemoteEl(id)
            }
            this.emit(CLIENT_EVENTS.CONF_PARTICIPANT_REMOVED, {...participant})
        }
    }

    _removeAllParticipants() {
        const participantsList = [ ...this.participants.keys() ]
        participantsList.forEach(id => this._removeParticipant(id))
    }

    async _startTranslation () {
        //add self peer
        this._logInfo('_startTranslation: called', {
            meetingId: this.meetingId,
            audioMixing: this.audioMixing,
            subscribeMode: this.subscribeMode,
        })
        if (this.webRTCClient) {
            this.webRTCClient.stop()
            this.webRTCClient = null
        }
        this.webRTCClient = new WebRTCClient({
            videoPlugText: this.name,
            video: true,
            iceServers: this.iceServers,
            logger: this.webrtcLogger,
            debug: this.debug,
        })
        await this._connectParticipant(this.meetingUserId)
        this._logDebug('_startTranslation: local mounted')
        this._connectRemoteParticipants().catch((e) => {
            this._logDebug('_connectRemoteParticipants: error')
            this._logError(e, '_connectRemoteParticipants: error')
        })
        if (this.sharing) setTimeout(() => this._onSharingStart(this.sharing), 5000) //@todo
    }

    async _stopTranslation () {
        this._logInfo('_stopTranslation: called', {meetingId: this.meetingId})
        this._removeAllParticipants()
        if (this.sharing) this._onSharingStop({ meetingId: this.meetingId })
        if (this.webRTCClient) {
            this.webRTCClient.stop()
            this.webRTCClient = null
        }
    }

    async _connectRemoteParticipants () {
        this._logDebug('_connectRemoteParticipants: called', {count: this.participants.size})
        let participantList = [...this.participants.values()]
        for (let i = 0, listCount = participantList.length; i < listCount; i++) {
            this._connectParticipant(participantList[i].id)
        }
        this._logDebug('_connectRemoteParticipants: remote participants mounted')

    }

    async _connectParticipant (id) {
        this._logDebug('_connectParticipant: connection started', {id})
        let participant = this._getParticipant(id)
        if (!participant) {
            this._logDebug('_connectParticipant: participant not found', {id})
            return
        }
        if (participant.connectionState !== PARTICIPANT_CONNECTION_STATE.DISCONNECTED) {
            this._logDebug('_connectParticipant: connectionState not disconnected, ignore', {id, state: participant.connectionState})
            return
        }
        participant.connectionState = PARTICIPANT_CONNECTION_STATE.CONNECTING
        participant.options = this.confOptions
        participant.attempts = this.meetingInitAttempts
        this._addParticipantDomElements(id)
        this.emit(CLIENT_EVENTS.CONF_PARTICIPANT_ADDED, {...participant})
        if (participant[PARTICIPANT_PROPS.IS_ME]) {
            this._logDebug('_connectParticipant: add local', {id})
            if (this.audioMixing) {
                participant.direction = 'sendrecv'
            } else {
                participant.direction = 'sendonly'
            }
            participant.videoElId = '#localConferenceVideo'
        } else {
            this._logDebug('_connectParticipant: add remote', {id})
            participant.direction = 'recvonly'
            participant.videoElId = '#remoteConferenceVideo' + id
            participant.audioElId = '#remoteConferenceAudio' + id
        }

        if (!participant[PARTICIPANT_PROPS.IS_ME] && this.subscribeMode === true && !participant.offer) {
            this._logDebug('_connectParticipant: waiting for offer in subscribe mode', {id})
            return
        }

        this._logDebug('_connectParticipant: _initConferenceMedia called', {
            id,
            direction: participant.direction,
            hasOffer: Boolean(participant.offer),
        })
        await this._initConferenceMedia(participant)
        this._logDebug('_connectParticipant: _initConferenceMedia resolved', {id})
    }

    async _connectSharing () {
        if (this.share && this.sharing) {
            this.sharing.attempts = this.meetingInitAttempts
            if (this.sharing.userId !== this.meetingUserId) {
                if (this.subscribeMode && !this.sharing[SHARING_PROPS.MY_SHARING] && !this.sharing.offer) {
                    return
                }
                this._addShareDomElements()
                this.webRTCClient.setSharingEl(this.shareWrapperEl.querySelector('#sharing'))
                this.sharing.direction = (this.sharing[SHARING_PROPS.MY_SHARING]) ? 'sendonly' : 'recvonly'
                this.sharing.onRemoteStream = (remoteStream) => {
                    this._logDebug('_connectSharing: remoteStream', describeStream(remoteStream))
                    this.webRTCClient.setSharingStream(remoteStream)
                }
                this._logDebug('_connectSharing: start', {
                    id: this.sharing.id,
                    direction: this.sharing.direction,
                    mySharing: this.sharing[SHARING_PROPS.MY_SHARING],
                })
                await this._initConferenceMedia(this.sharing)
            }
        }
    }

    _addAudioMixingDomElement () {
        if (!this.audioMixing) return
        if (!document.querySelector('#audioMixing')) {
            let audioMixing = document.createElement('audio')
            audioMixing.setAttribute('id', 'audioMixing')
            audioMixing.setAttribute('style', 'display: none;')
            audioMixing.muted = false
            audioMixing.autoplay = true
            document.querySelector('body').appendChild(audioMixing)
            this._logDebug('audioMixing element added', describeMediaElement(audioMixing))
        }
    }

    _removeAudioMixingDomElement () {
        const el = document.querySelector('#audioMixing')
        if (el) {
            this._logDebug('audioMixing element removed')
            el.remove()
        }
    }

    _addShareDomElements () {
        if (!this.shareWrapperEl) return
        let shareVideo = document.createElement('video')
        shareVideo.setAttribute('id', 'sharing')
        shareVideo.setAttribute('style', 'width: 100%; height: 100%;')
        shareVideo.muted = true
        shareVideo.autoplay = true
        this.shareWrapperEl.appendChild(shareVideo)
        this.share.setAttribute('style', 'width: 100%; height: 100%; display: block;')
        this._logDebug('share dom added', describeMediaElement(shareVideo))
    }

    _removeShareDomElements () {
        if (!this.shareWrapperEl) return
        this.share.setAttribute('style', 'width: 100%; height: 100%; display: none;')
        this.shareWrapperEl.querySelector('#sharing').remove()
        this._logDebug('share dom removed')
    }

    _addParticipantDomElements (id) {
        if (this.videoWrapperEl.querySelector(`[data-jid="${id}"]`)) return

        const isMe = id === this.meetingUserId

        let wrapperEl = document.createElement('span')
        wrapperEl.setAttribute('data-jid', id)
        wrapperEl.setAttribute('class', 'videocontainer' + (isMe ? ' view_self' : ''))

        const elId = isMe ? 'localConferenceVideo' : 'remoteConferenceVideo' + id
        const local = document.createElement('video')
        local.setAttribute('id', elId)
        if (isMe) local.muted = true
        local.autoplay = true
        local.setAttribute('style', 'background-color: grey')
        wrapperEl.appendChild(local)
        if (!isMe && !this.audioMixing) {
            const audioEl = document.createElement('audio')
            audioEl.setAttribute('id', `remoteConferenceAudio${id}`)
            audioEl.setAttribute('style', 'display: none;')
            audioEl.autoplay = true
            audioEl.playsInline = true
            audioEl.muted = false
            wrapperEl.appendChild(audioEl)
            this._logDebug('remote audio dom added', describeMediaElement(audioEl))
        }

        this.videoWrapperEl.appendChild(wrapperEl)
    }

    _removeParticipantDomElements (id) {
        let el = this.videoWrapperEl.querySelector(`[data-jid="${id}"]`)
        if (el) el.remove()
    }

    _getLocalOptions () {
        let options = {
            audio: this.mic,
            video: this.cam,
        }
        let activeMicrophone = this.activeDevices[MEDIA_DEVICES.AUDIO_INPUT]
        if (options.audio && activeMicrophone && activeMicrophone !== 'default')
            options.audio = {deviceId: {exact: activeMicrophone}}
        let activeCamera = this.activeDevices[MEDIA_DEVICES.VIDEO_INPUT]
        if (options.video && activeCamera && activeCamera !== 'default')
            options.video = {deviceId: {exact: activeCamera}}
        return options
    }

    _scheduleInitConferenceMedia (participant, attempt = 0) {
        if (!participant) return
        if (participant._initScheduled && attempt === 0) return
        participant._initScheduled = true
        const maxAttempts = 5
        const videoElId = participant.videoElId
        const remoteEl = videoElId ? document.querySelector(videoElId) : null
        if (!remoteEl) {
            if (attempt >= maxAttempts) {
                this._logWarn('_scheduleInitConferenceMedia: remote video element not found', {id: participant.id, videoElId})
                participant._initScheduled = false
                return
            }
            setTimeout(() => this._scheduleInitConferenceMedia(participant, attempt + 1), 0)
            return
        }
        Promise.resolve(this._initConferenceMedia(participant))
            .finally(() => {
                participant._initScheduled = false
            })
    }

    async _initConferenceMedia (participant) {
        if (!participant) return
        this._logDebug('initConferenceMedia', {
            id: participant.id,
            videoElId: participant.videoElId,
            direction: participant.direction,
            isMe: participant[PARTICIPANT_PROPS.IS_ME],
        })
        if (participant[PARTICIPANT_PROPS.IS_ME]) {
            const options = {...this._getLocalOptions()}
            if (!options.video) options.video = true
            const localEl = document.querySelector(participant.videoElId)
            if (!localEl) this._logWarn('initConferenceMedia: local video element not found', {id: participant.id, videoElId: participant.videoElId})
            await this.webRTCClient.startLocal(localEl, options)
            if (!this.mic) this.webRTCClient.mute()
            if (!this.cam) this.webRTCClient.muteVideo()
            const localStream = this.webRTCClient.getLocalStream()
            this._logDebug('local stream ready', describeStream(localStream))
            this._logStreamIssues('local stream', localStream, {audio: this.mic, video: this.cam})
        } else {
            const remoteEl = document.querySelector(participant.videoElId)
            const participantId = participant[PARTICIPANT_PROPS.ID]
            const audioEl = (!this.audioMixing && !participant[PARTICIPANT_PROPS.IS_ME])
                ? document.querySelector(participant.audioElId)
                : null
            if (!remoteEl) this._logWarn('initConferenceMedia: remote video element not found', {id: participant.id, videoElId: participant.videoElId})
            this.webRTCClient.addRemoteEl(remoteEl, audioEl, participantId)
        }

        const localStream = (participant.localStream !== undefined)
            ? participant.localStream
            : ((participant[PARTICIPANT_PROPS.IS_ME] || participant.direction !== 'recvonly') ? this.webRTCClient.getLocalStream() : null)

        const onRemoteStream = participant.onRemoteStream || ((remoteStream) => {
            if (participant[PARTICIPANT_PROPS.IS_ME]) {
                if (this.audioMixing) {
                    this._addAudioMixingDomElement()
                    this.webRTCClient.setAudioMixingEl(document.querySelector('#audioMixing'))
                    this.webRTCClient.setAudioMixingStream(remoteStream)
                    this._logDebug('audioMixing: remote stream set', describeStream(remoteStream))
                }
            } else {
                this.webRTCClient.setRemoteStream(remoteStream, participant[PARTICIPANT_PROPS.ID])
                this._logDebug('remote stream set', {
                    id: participant[PARTICIPANT_PROPS.ID],
                    stream: describeStream(remoteStream),
                })
                this._logStreamIssues('remote stream', remoteStream, {
                    audio: participant[PARTICIPANT_PROPS.MIC] !== false,
                    video: participant[PARTICIPANT_PROPS.CAM] !== false,
                })
            }
        })

        const onIceCandidate = (candidate) => {
            let data = {
                id: participant.id, meetingId: this.meetingId, candidate: {
                    candidate: candidate.candidate,
                    sdpMLineIndex: candidate.sdpMLineIndex,
                    sdpMid: candidate.sdpMid,
                },
            }
            this._logDebug('send-meeting-add-candidate', data)
            this.transport.sendMeetingAddCandidate(data)
        }

        if (participant.offer) {
            const offerSdp = participant.offer.sdp
            if (participant._answerInProgress && participant._answerOfferSdp === offerSdp) {
                this._logDebug('initConferenceMedia: answer already in progress', {id: participant.id})
                return
            }
            if (participant._lastAnsweredOfferSdp === offerSdp) {
                this._logDebug('initConferenceMedia: offer already answered', {id: participant.id})
                return
            }
            participant._answerInProgress = true
            participant._answerOfferSdp = offerSdp
            try {
                const answer = await this.webRTCClient.createAnswer({
                    id: participant.id,
                    iceServers: this.iceServers,
                    localStream,
                    remoteOffer: participant.offer,
                    direction: participant.direction,
                    onRemoteStream,
                    onIceCandidate
                })
                participant.sdp = answer.sdp
                await this._sendMeetingAnswer(participant)
                participant._lastAnsweredOfferSdp = offerSdp
            } finally {
                if (participant._answerOfferSdp === offerSdp) {
                    participant._answerInProgress = false
                    participant._answerOfferSdp = null
                }
            }
        } else {
            const offer = await this.webRTCClient.createOffer({
                id: participant.id,
                iceServers: this.iceServers,
                localStream,
                direction: participant.direction,
                onRemoteStream,
                onIceCandidate
            })
            participant.sdp = offer.sdp
            await this._sendMeetingOffer(participant)
        }
    }

    async _sendMeetingOffer (participant) {
        const data = {id: participant.id, meetingId: this.meetingId, sdp: participant.sdp}
        if (!participant.offerSend) {
            participant.offerSend = true
            this._logDebug('send-meeting-offer', data)
            const response = await this.transport.sendMeetingOffer(data)
            if (response && response.error) {
                return this._logError(new Error(response.error), 'send-meeting-offer error', response)
            }
            this._logDebug('send-meeting-offer response', response)
        }
    }

    async _sendMeetingAnswer (participant) {
        const data = {id: participant.id, meetingId: this.meetingId, sdp: participant.sdp}
        if (!participant.answerSend) {
            participant.answerSend = true
            this._logDebug('send-meeting-answer', data)
            const response = await this.transport.sendMeetingAnswer(data)
            if (response && response.error) {
                return this._logError(new Error(response.error), 'send-meeting-answer error', response)
            }
            this._logDebug('send-meeting-answer response', response)
        }
    }


}

export default MeetingsClient
